import React, { useState, useEffect } from 'react';
import notificationService from '../services/notificationService.js';
import { CloseIcon } from './icons';

const NotificationPanel = ({ 
  isOpen, 
  onClose, 
  isMobile 
}) => {
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    // Subscribe to notification updates
    const listener = (updatedNotifications) => {
      setNotifications(updatedNotifications);
    };
    
    notificationService.addListener(listener);
    
    // Get initial notifications
    setNotifications(notificationService.getNotifications());
    
    return () => {
      notificationService.removeListener(listener);
    };
  }, []);

  const handleMarkAsRead = (notificationId) => {
    notificationService.markAsRead(notificationId);
  };

  const handleMarkAllAsRead = () => {
    notificationService.markAllAsRead();
  };

  const handleRemoveNotification = (notificationId) => {
    notificationService.removeNotification(notificationId);
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  // Early return if not open
  if (!isOpen) {
    return null;
  }

  // For mobile, we render a modal-like notification panel
  if (isMobile) {
    return (
      <>
        {/* Mobile notification overlay */}
        <div 
          className="fixed inset-0 bg-black/50 z-40 sm:hidden"
          onClick={onClose}
        />
        
        {/* Mobile notification panel */}
        <aside 
          className="fixed top-0 right-0 h-full bg-dark-surface border-l border-dark-border p-6 z-50 flex flex-col transition-transform duration-300 ease-in-out sm:hidden"
          style={{ width: '90%', maxWidth: '320px' }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-dark-text-primary">
              Notifications {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 ml-2">
                  {unreadCount}
                </span>
              )}
            </h2>
            <button 
              onClick={onClose}
              className="p-2 rounded-md text-dark-text-secondary hover:bg-dark-bg"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>
          
          {renderNotificationContent()}
        </aside>
      </>
    );
  }

  // Desktop notification panel
  function renderNotificationContent() {
    if (notifications.length === 0) {
      return (
        <div className="flex items-center justify-center text-center h-full">
          <p className="text-dark-text-secondary">No notifications</p>
        </div>
      );
    }

    return (
      <>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-medium text-dark-text-primary">
            {unreadCount} unread {unreadCount === 1 ? 'notification' : 'notifications'}
          </h3>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="text-sm text-brand-primary hover:underline"
            >
              Mark all as read
            </button>
          )}
        </div>

        <div className="flex-grow overflow-y-auto space-y-3 -mr-2 pr-2">
          {[...notifications].reverse().map(notification => (
            <div 
              key={notification.id}
              className={`p-3 rounded-md border ${
                notification.read 
                  ? 'bg-dark-bg border-dark-border' 
                  : 'bg-brand-primary/10 border-brand-primary/30'
              }`}
            >
              <div className="flex justify-between">
                <h4 className="font-medium text-dark-text-primary">{notification.title}</h4>
                {!notification.read && (
                  <span className="w-2 h-2 bg-brand-primary rounded-full"></span>
                )}
              </div>
              
              <p className="text-sm text-dark-text-secondary mt-1">
                {notification.message}
              </p>
              
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-dark-text-secondary">
                  {new Date(notification.timestamp).toLocaleString()}
                </span>
                
                <div className="flex space-x-2">
                  {!notification.read && (
                    <button
                      onClick={() => handleMarkAsRead(notification.id)}
                      className="text-xs text-brand-primary hover:underline"
                    >
                      Mark as read
                    </button>
                  )}
                  <button
                    onClick={() => handleRemoveNotification(notification.id)}
                    className="text-xs text-red-500 hover:underline"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <aside className="w-80 bg-dark-surface border-l border-dark-border flex-shrink-0 p-6 z-20 flex flex-col hidden sm:flex">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-dark-text-primary">
          Notifications {unreadCount > 0 && (
            <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 ml-2">
              {unreadCount}
            </span>
          )}
        </h2>
        <button 
          onClick={onClose}
          className="p-2 rounded-md text-dark-text-secondary hover:bg-dark-bg"
        >
          <CloseIcon className="w-5 h-5" />
        </button>
      </div>
      {renderNotificationContent()}
    </aside>
  );
};

export default NotificationPanel;